import { useState } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDateVN, toISODate } from '@rs/contracts'
import { Card, Modal } from '@rs/ui'
import { tDynamic } from '@rs/ui/i18n'
import { qk, useList, useOne } from '../../shared/queries.ts'
import { fetchCustomers } from '../catalog/api.ts'
import { fetchOpenOrders, type OpenOrders } from '../stats/api.ts'

type Line = OpenOrders['noLeg'][number]

/**
 * ══ 📌 DẢI "ĐƠN ĐANG MỞ" — chép khung trang `AzPage` (`proship-trans/src/pages/Az.tsx:470-476`, `:613-660`) ════════════════
 * ⭐ Bản cũ dựng dải ở KHUNG TRANG và sau khi loại trừ bảy mục thì `/az/vendor-pnl` là màn DUY NHẤT còn dùng (`Az.tsx:609`).
 *   Điều kiện hiện chỉ xét MÀN, không xét quyền giá vốn ⇒ người chưa được tick "xem giá thầu phụ" vẫn thấy dải nằm trên thẻ 🔒
 *   (R0221) — nên dải đọc cửa RIÊNG `GET /az/don-dang-mo` (quyền đọc đơn), không ăn theo báo cáo giá vốn.
 * ⚠ KHÔNG ăn theo bộ lọc ba bảng bên dưới: nó nói về TOÀN BỘ đơn đang mở trong phạm vi người xem.
 * ⭐ Bấm số ⇒ popup năm cột Mã đơn · Khách hàng · Tuyến · Hạn giao · Trạng thái (`Az.tsx:634-659`): hạn giao ĐÃ QUA tô đỏ đậm,
 *   tuyến rỗng hiện "—" (R0225 — trước dùng hộp drill chung, mất màu đỏ và để ô trống).
 * ⚠ Màn gắn dải PHẢI nạp nhóm chuỗi `az` (cổng `i18n-key-group`).
 */
export const OpenOrdersStrip = (): JSX.Element => {
  const { t } = useTranslation()
  const { data } = useOne(qk.openOrders(), fetchOpenOrders)
  const { rows: customers } = useList(qk.customers(), fetchCustomers)
  const [drill, setDrill] = useState<{ title: string; list: readonly Line[] } | null>(null)
  const today = toISODate()
  const cusName = (id: string | null): string => (customers ?? []).find((c) => c.id === id)?.name ?? '—'
  const TH = 'px-3 py-2 text-[10px] font-bold text-slate-400 uppercase whitespace-nowrap text-center'

  return (
    <>
      <Card className="p-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{t('az.openOrders.title')}</span>
        {([
          { k: 'noLeg', label: t('az.kpi.noLeg'), list: data?.noLeg ?? [], tone: 'amber' },
          { k: 'running', label: t('az.kpi.running'), list: data?.running ?? [], tone: 'sky' },
          { k: 'late', label: t('az.kpi.late'), list: data?.late ?? [], tone: 'red' },
        ] as const).map((s) => (
          <span key={s.k} className="flex items-baseline gap-1.5 whitespace-nowrap">
            <span className="text-slate-500">{s.label}</span>
            <button type="button" title={t('az.openOrders.drillHint')}
                    onClick={() => { setDrill({ title: t('az.openOrders.drillTitle', { label: s.label, n: s.list.length }), list: s.list }) }}
                    className={`text-sm font-bold tabular-nums underline decoration-dotted underline-offset-2 hover:opacity-70 ${
                      s.tone === 'sky' ? 'text-sky-600' : s.list.length === 0 ? 'text-slate-300' : s.tone === 'amber' ? 'text-amber-600' : 'text-red-600'}`}>
              {s.list.length.toLocaleString('vi-VN')}
            </button>
          </span>
        ))}
      </Card>

      {drill !== null && (
        <Modal title={drill.title} onClose={() => { setDrill(null) }} className="max-w-3xl">
          <div className="overflow-x-auto max-h-[65vh]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className={TH}>{t('az.col.ref')}</th><th className={TH}>{t('az.openOrders.colCustomer')}</th>
                  <th className={TH}>{t('az.openOrders.colRoute')}</th><th className={TH}>{t('az.col.dueAt')}</th>
                  <th className={TH}>{t('az.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {drill.list.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-300 italic">{t('az.openOrders.empty')}</td></tr>}
                {drill.list.map((j) => {
                  const overdue = (j.dueAt ?? '') !== '' && (j.dueAt ?? '') < today
                  return (
                    <tr key={j.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono font-semibold text-slate-700 whitespace-nowrap">{j.ref}</td>
                      <td className="px-3 py-2 text-right text-slate-600 whitespace-nowrap">{cusName(j.customerId)}</td>
                      <td className="px-3 py-2 text-right text-slate-500 whitespace-nowrap">{(j.routeName ?? '') === '' ? '—' : j.routeName}</td>
                      <td className={`px-3 py-2 text-right whitespace-nowrap ${overdue ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                        {(j.dueAt ?? '') === '' ? '—' : formatDateVN(j.dueAt ?? '')}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-600 whitespace-nowrap">{tDynamic(t, `az.ordSt.${j.orderStatus}`)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </>
  )
}
